import { useEffect } from 'react';

import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { MarkerTypes } from 'hooks/use-map';
import { setActiveStoreId } from 'pages/store-locator/ducks/stores';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { useServiceModeContext } from 'state/service-mode';
import { isMobileOrderingAvailable, isRestaurantOpen } from 'utils/restaurant';
import { isCatering } from 'utils/service-mode';

export default function useMarkers({
  createMarker,
  markers,
  map,
  onClickMarker,
  panTo,
  nearbyStores,
  favStores,
  activeStoreId,
  storeLocatorDispatch
}) {
  const { libraryLoaded: libLoaded } = useGoogleGeolocationLibrary();
  const { userCoordinates } = useGeolocation();
  const { region } = useLocale();
  const { serviceMode } = useServiceModeContext();
  useEffect(() => {
    if (!activeStoreId) {
      return;
    }

    // inactivate all current markers before we activate the current marker
    if (markers.current.active) {
      markers.current.active.inactive();
    }
    const activeMarker = markers.current[activeStoreId];

    if (activeMarker) {
      markers.current.active = activeMarker;
      activeMarker.active();
      panTo({ lat: activeMarker.lat, lng: activeMarker.lng });
    }
  }, [activeStoreId, markers, panTo]);

  useEffect(() => {
    if (!libLoaded) {
      return;
    }

    nearbyStores.forEach(store => {
      const location = { lat: store.latitude, lng: store.longitude };

      const diningRoomOpen = isRestaurantOpen(store.diningRoomHours);
      const driveThruOpen = isRestaurantOpen(store.driveThruHours);
      const open = driveThruOpen || diningRoomOpen;

      const isCorrectRegion =
        store &&
        store.physicalAddress &&
        store.physicalAddress.country &&
        store.physicalAddress.country.toUpperCase().startsWith(region);

      const marker = createMarker({
        type: MarkerTypes.Store,
        location,
        onClick: () => {
          onClickMarker(store);
          storeLocatorDispatch(setActiveStoreId(store._id));
        },
        introAnim: true
      });

      if (marker) {
        markers.current[store._id] = {
          active: marker.active,
          inactive: marker.inactive,
          disabled: marker.disabled,
          favOpen: marker.favOpen,
          favClosed: marker.favClosed,
          ...location
        };

        if (store._id === activeStoreId) {
          markers.current.active = marker;
        }
        const isStoreFavorite = favStores?.some(favStore => favStore._id === store._id);
        if (isStoreFavorite && store._id !== activeStoreId) {
          marker.favOpen();
        }
        const isCateringServiceMode = isCatering(serviceMode);
        const isStoreAvailable =
          isCateringServiceMode || (isMobileOrderingAvailable(store) && open && isCorrectRegion);
        if (isStoreAvailable) {
          return;
        }
        if (store._id === activeStoreId) {
          marker.active();
        } else if (isStoreFavorite) {
          marker.favClosed();
        } else {
          marker.disabled();
        }
      }
    });
  }, [
    nearbyStores,
    libLoaded,
    createMarker,
    activeStoreId,
    markers,
    onClickMarker,
    region,
    favStores,
    storeLocatorDispatch,
    serviceMode
  ]);
  // Create a marker for the users coordinates
  useEffect(() => {
    if (libLoaded && map) {
      createMarker({ type: MarkerTypes.User, location: userCoordinates });
    }
  }, [userCoordinates, libLoaded, map, createMarker]);
}
